// The stage's two kinds of motion: timed tweens between views, and the
// shelf and carousel rows, which glide after a finger or the wheel.

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
export const approach = (dt: number, rate: number) => 1 - Math.exp(-dt * rate)
// Anything still further than this from where it's heading (in booklet
// steps, lift shares or radians: well under a tenth of a pixel) is still
// moving, and keeps the stage drawing.
export const SETTLED = 1e-4

export class Tween {
  value: number
  private from = 0
  private to = 0
  private start = 0
  private duration = 0

  constructor(value: number) {
    this.value = value
    this.to = value
  }

  get target() {
    return this.to
  }

  get running() {
    return this.duration > 0
  }

  set(value: number) {
    this.value = this.from = this.to = value
    this.duration = 0
  }

  go(to: number, duration: number, now: number) {
    this.from = this.value
    this.to = to
    this.start = now
    this.duration = Math.max(1, duration * Math.abs(to - this.value))
  }

  update(now: number) {
    if (!this.duration) return
    const t = clamp01((now - this.start) / this.duration)
    this.value = this.from + (this.to - this.from) * t
    if (t >= 1) this.duration = 0
  }
}

// Scrolling in steps of one booklet: a finger or wheel drives a target, the
// row glides to it, and a fling carries on with the finger's speed, easing
// out like a thrown sheet; past either end it pulls back. A snapping
// scroller (the carousel) always comes to rest on a whole booklet.
export class Scroller {
  pos = 0
  target = 0
  max = 0
  constructor(private readonly snap = false) {}
  private dragFrom = 0
  private samples: { t: number; v: number }[] = []
  dragging = false

  reset(to = 0) {
    this.pos = this.target = to
  }

  grab(now: number) {
    this.dragging = true
    this.dragFrom = this.target = this.pos
    this.samples = [{ t: now, v: this.pos }]
  }

  drag(offset: number, now: number) {
    let next = this.dragFrom + offset
    const over = next < 0 ? next : next > this.max ? next - this.max : 0
    if (over) next -= over * 0.65
    this.target = this.pos = next
    this.samples.push({ t: now, v: next })
    while (this.samples.length > 2 && now - this.samples[0]!.t > 90)
      this.samples.shift()
  }

  release() {
    this.dragging = false
    const a = this.samples[0]
    const b = this.samples[this.samples.length - 1]
    const dt = a && b ? (b.t - a.t) / 1000 : 0
    const v = dt > 0.012 ? (b!.v - a!.v) / dt : 0
    const flung = this.pos + v * 0.32
    // A carousel flick moves at most a couple of booklets.
    this.target = this.clamp(
      this.snap
        ? Math.round(
            Math.min(this.pos + 2, Math.max(this.pos - 2, flung)) +
              (Math.abs(v) > 1.2 ? Math.sign(v) * 0.4 : 0),
          )
        : flung,
    )
  }

  nudge(delta: number) {
    this.target = this.clamp(
      this.snap ? Math.round(this.target + delta) : this.target + delta,
    )
  }

  private clamp(v: number) {
    return Math.min(this.max, Math.max(0, v))
  }

  // Returns whether the row is still gliding.
  step(dt: number) {
    if (!this.dragging) this.target = this.clamp(this.target)
    const gap = this.target - this.pos
    this.pos += gap * approach(dt, 7)
    return Math.abs(gap) > SETTLED
  }
}
